/**
 * ISO 4217 currency lookup — lightweight static map.
 * Covers the currencies most likely to appear in Life4Earth communities
 * across the 8 supported locales (en, ar, fr, de, uk, sv, ru, es).
 *
 * DEV-576
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  /** Number of decimal places (most are 2; JPY/KRW are 0). */
  decimals: number;
}

const CURRENCIES: CurrencyInfo[] = [
  // Major world currencies
  { code: "USD", symbol: "$",   name: "US Dollar",             decimals: 2 },
  { code: "EUR", symbol: "€",   name: "Euro",                  decimals: 2 },
  { code: "GBP", symbol: "£",   name: "British Pound",         decimals: 2 },
  { code: "JPY", symbol: "¥",   name: "Japanese Yen",          decimals: 0 },
  { code: "CNY", symbol: "¥",   name: "Chinese Yuan",          decimals: 2 },
  { code: "CHF", symbol: "Fr.", name: "Swiss Franc",            decimals: 2 },
  { code: "CAD", symbol: "C$",  name: "Canadian Dollar",        decimals: 2 },
  { code: "AUD", symbol: "A$",  name: "Australian Dollar",      decimals: 2 },
  { code: "NZD", symbol: "NZ$", name: "New Zealand Dollar",     decimals: 2 },

  // Life4Earth locale currencies
  { code: "SEK", symbol: "kr",  name: "Swedish Krona",          decimals: 2 },
  { code: "NOK", symbol: "kr",  name: "Norwegian Krone",        decimals: 2 },
  { code: "DKK", symbol: "kr",  name: "Danish Krone",           decimals: 2 },
  { code: "UAH", symbol: "₴",   name: "Ukrainian Hryvnia",      decimals: 2 },
  { code: "RUB", symbol: "₽",   name: "Russian Ruble",          decimals: 2 },
  { code: "SAR", symbol: "﷼",   name: "Saudi Riyal",            decimals: 2 },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham",             decimals: 2 },
  { code: "EGP", symbol: "E£",  name: "Egyptian Pound",         decimals: 2 },
  { code: "MAD", symbol: "MAD", name: "Moroccan Dirham",        decimals: 2 },
  { code: "MXN", symbol: "MX$", name: "Mexican Peso",           decimals: 2 },
  { code: "BRL", symbol: "R$",  name: "Brazilian Real",         decimals: 2 },
  { code: "CLP", symbol: "CL$", name: "Chilean Peso",           decimals: 0 },
  { code: "COP", symbol: "COL$",name: "Colombian Peso",         decimals: 0 },
  { code: "ARS", symbol: "AR$", name: "Argentine Peso",         decimals: 2 },

  // African currencies (key Life4Earth regions)
  { code: "NGN", symbol: "₦",   name: "Nigerian Naira",         decimals: 2 },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling",        decimals: 2 },
  { code: "ZAR", symbol: "R",   name: "South African Rand",     decimals: 2 },
  { code: "GHS", symbol: "GH₵", name: "Ghanaian Cedi",          decimals: 2 },

  // Asian currencies
  { code: "INR", symbol: "₹",   name: "Indian Rupee",           decimals: 2 },
  { code: "KRW", symbol: "₩",   name: "South Korean Won",       decimals: 0 },
  { code: "IDR", symbol: "Rp",  name: "Indonesian Rupiah",      decimals: 0 },
  { code: "PHP", symbol: "₱",   name: "Philippine Peso",        decimals: 2 },
  { code: "THB", symbol: "฿",   name: "Thai Baht",              decimals: 2 },
];

/** Code → CurrencyInfo lookup map (case-insensitive key). */
const BY_CODE = new Map<string, CurrencyInfo>();
for (const c of CURRENCIES) {
  BY_CODE.set(c.code.toUpperCase(), c);
}

/** Get currency info by ISO 4217 code. Returns USD as fallback. */
export function getCurrency(code: string | null | undefined): CurrencyInfo {
  if (!code) return BY_CODE.get("USD")!;
  return BY_CODE.get(code.toUpperCase()) ?? BY_CODE.get("USD")!;
}

/** Get the symbol for a currency code. */
export function getCurrencySymbol(code: string | null | undefined): string {
  return getCurrency(code).symbol;
}

/**
 * Format a numeric value with the currency symbol.
 * Examples:
 *   formatCurrency(50000, "USD")     → "$50,000"
 *   formatCurrency(1234.5, "EUR")    → "€1,234.50"
 *   formatCurrency(5000000, "JPY")   → "¥5,000,000"
 */
export function formatCurrency(value: number, code: string | null | undefined): string {
  const cur = getCurrency(code);
  const formatted = value.toLocaleString("en-US", {
    minimumFractionDigits: cur.decimals,
    maximumFractionDigits: cur.decimals,
  });
  return `${cur.symbol}${formatted}`;
}

/**
 * Format a numeric value as a compact string (K, M, B) with currency symbol.
 * Examples:
 *   formatCurrencyCompact(1500, "USD")     → "$1.5K"
 *   formatCurrencyCompact(2500000, "EUR")  → "€2.5M"
 */
export function formatCurrencyCompact(value: number, code: string | null | undefined): string {
  const cur = getCurrency(code);
  const abs = Math.abs(value);
  let formatted: string;
  if (abs >= 1_000_000_000) formatted = (value / 1_000_000_000).toFixed(1) + "B";
  else if (abs >= 1_000_000) formatted = (value / 1_000_000).toFixed(1) + "M";
  else if (abs >= 1_000) formatted = (value / 1_000).toFixed(1) + "K";
  else formatted = value.toFixed(cur.decimals);
  return `${cur.symbol}${formatted}`;
}

/** All available currencies, sorted by code. Used for dropdown rendering. */
export const ALL_CURRENCIES: ReadonlyArray<CurrencyInfo> = [...CURRENCIES].sort((a, b) => a.code.localeCompare(b.code));
